/**
 * Entity factories and normalizers (spec § 3).
 *
 * The defaults here are transcribed from the prototype and are load-bearing:
 * `status: 'active'`, `type: 'Not Sure'`, `cert: 'Need to Verify'` and
 * `decision: 'Maybe'` are what the screens and rules expect to find. A new fair
 * is "At the fair", not "Upcoming"; a new product is a Maybe, not unrated.
 *
 * Money fields are STRINGS as typed (spec § 3) — parsed with parseFloat at the
 * point of use, empty meaning 0. Do not "helpfully" store them as numbers:
 * '0.50' and '.50' and '' all have to round-trip exactly as the buyer typed
 * them, and a number would lose that.
 *
 * Timestamps are epoch ms.
 */

import { DEFAULT_FX } from './constants';

/**
 * Random id, matching the prototype: 8 chars of randomness plus 4 of
 * timestamp, so ids sort roughly by creation and collisions are negligible.
 * Ids are generated on the CLIENT because capture happens offline — the server
 * never mints them.
 */
export const uid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

export const nowMs = () => Date.now();

/* ── Fair (spec § 3.1) ──────────────────────────────────────────────────── */

export function newFair(overrides) {
  return {
    id: uid(),
    name: '',
    location: '',
    dates: '',
    status: 'active',
    plan: [],
    fx: { ...DEFAULT_FX },
    budget: '',
    forwarder: { name: '', phone: '', wechat: '', addressCN: '', addressEN: '' },
    checklist: [],
    createdAt: nowMs(),
    updatedAt: nowMs(),
    ...overrides,
  };
}

/** PlanItem (spec § 3.1). "Found" is `found === true || matches.length > 0`. */
export function newPlanItem(overrides) {
  return {
    id: uid(),
    name: '',
    category: '',
    target: '',
    qty: '',
    notes: '',
    found: false,
    supplierId: '',
    matches: [],
    ...overrides,
  };
}

export function newChecklistItem(text) {
  return { id: uid(), text, done: false };
}

/* ── Supplier (spec § 3.2) ──────────────────────────────────────────────── */

export function newSupplier(overrides) {
  const t = nowMs();
  return {
    id: uid(),
    fairId: '',
    company: '',
    booth: '',
    phase: '',
    hall: '',
    type: 'Not Sure',
    contacts: [],
    products: [],
    photos: [],
    videos: [],
    followUps: [],
    chinaNotes: '',
    review: {},
    decision: null,
    inShortlist: false,
    manualShortlist: false,
    outreach: { sentAt: null, replied: false },
    // createdAt doubles as the "Visited" date (spec § 3.2).
    createdAt: t,
    updatedAt: t,
    ...overrides,
  };
}

/* ── Product (spec § 3.3) ───────────────────────────────────────────────── */

export function newProduct(overrides) {
  return {
    id: uid(),
    name: '',
    category: '',
    description: '',
    price: '',
    currency: 'USD',
    priceUnit: 'pc',
    qty: '',
    moq: '',
    bestPrice: '',
    validUntil: '',
    tiers: [],
    fobPort: '',
    paymentTerms: '',
    productionTime: '',
    sample: 'No',
    samplePrice: '',
    sampleShipping: '',
    sampleNotes: '',
    cartonL: '',
    cartonW: '',
    cartonH: '',
    lenUnit: 'cm',
    cartonWeight: '',
    wtUnit: 'kg',
    unitsPerCarton: '',
    cert: 'Need to Verify',
    certTypes: [],
    certOther: '',
    rating: 0,
    decision: 'Maybe',
    notes: '',
    nextStep: 'None',
    revisit: false,
    revisitReason: '',
    revisitAt: '',
    planId: '',
    sellPrice: '',
    samplePaid: '',
    samplePayMethod: '',
    sampleHandover: '',
    samplePickup: '',
    depositPaid: '',
    depositMethod: '',
    depositNotes: '',
    voiceNotes: [],
    createdAt: nowMs(),
    ...overrides,
  };
}

/* ── Contact (spec § 3.4) ───────────────────────────────────────────────── */

export function newContact(overrides) {
  return {
    id: uid(),
    name: '',
    role: '',
    phone: '',
    wechat: '',
    whatsapp: '',
    email: '',
    website: '',
    instagram: '',
    linkedin: '',
    other: '',
    primary: false,
    photos: [],
    ...overrides,
  };
}

/* ── FollowUp (spec § 3.5) ──────────────────────────────────────────────── */

export function newFollowUp(overrides) {
  return {
    id: uid(),
    type: 'Other',
    date: '',
    time: '',
    status: 'Pending',
    notes: '',
    productId: '',
    auto: false,
    ...overrides,
  };
}

/* ── Normalizers ────────────────────────────────────────────────────────── */
/**
 * The prototype backfilled missing sub-objects on every boot (its `ensureFair`)
 * because records written by an older build lack fields a newer one reads.
 * That problem is worse here, not better: a phone can hold records captured
 * before an update and sync them weeks later. So every record is normalized on
 * the way in, from storage AND from the server.
 */

export function normalizeFair(f) {
  if (!f) return null;
  return {
    ...newFair({ id: f.id, createdAt: f.createdAt, updatedAt: f.updatedAt }),
    ...f,
    fx: { ...DEFAULT_FX, ...(f.fx || {}) },
    forwarder: {
      name: '',
      phone: '',
      wechat: '',
      addressCN: '',
      addressEN: '',
      ...(f.forwarder || {}),
    },
    plan: (f.plan || []).map((it) => ({ ...newPlanItem({ id: it.id }), ...it })),
    checklist: f.checklist || [],
  };
}

export function normalizeSupplier(s) {
  if (!s) return null;
  return {
    ...newSupplier({ id: s.id, createdAt: s.createdAt, updatedAt: s.updatedAt }),
    ...s,
    contacts: (s.contacts || []).map((c) => ({ ...newContact({ id: c.id }), ...c })),
    products: (s.products || []).map((p) => ({
      ...newProduct({ id: p.id, createdAt: p.createdAt }),
      ...p,
      certTypes: p.certTypes || [],
      tiers: p.tiers || [],
      voiceNotes: p.voiceNotes || [],
    })),
    photos: s.photos || [],
    videos: s.videos || [],
    followUps: (s.followUps || []).map((f) => ({ ...newFollowUp({ id: f.id }), ...f })),
    review: s.review || {},
    outreach: { sentAt: null, replied: false, ...(s.outreach || {}) },
  };
}

/** Stamp updatedAt. Every mutation goes through this so sync ordering holds. */
export function touch(record) {
  return { ...record, updatedAt: nowMs() };
}

/** Soft delete — a tombstone, so a delete made offline survives the next pull. */
export function tombstone(record) {
  const t = nowMs();
  return { ...record, deletedAt: t, updatedAt: t };
}

export const isDeleted = (record) => !!(record && record.deletedAt);
